import React from 'react';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import useRUMView from 'common-datadog/useRUMView';
import ButtonPrimary from '../ButtonPrimary';
import { currencyFormat } from '../../constants';
import usePrequal from '../../resources/prequal/usePrequal';
import useApplication from '../../resources/applications/read';
import useForm from './useForm';
import Screen from '../Screen';
import Disclaimers from '../Disclaimers';

const InstallmentViewOffer = () => {
  useRUMView('installment-view-offer');
  const { data: prequal } = usePrequal();
  const { data: application } = useApplication();
  const min = application.attributes.min_loan;
  const max = application.attributes.max_non_recurring_ach_loan;
  const minFee = currencyFormat.format(min.amount - min.financed_amount);
  const maxFee = currencyFormat.format(max.amount - max.financed_amount);

  const {
    handleSubmit,
    isSubmitting,
  } = useForm();

  return (
    <Screen
      header="Congratulations!"
      subheader={(
        <>
          You have prequalified for an installment loan issued by WebBank, for up to
          {' '}
          <b>{currencyFormat.format(max.amount)}</b>
          .
        </>
        )}
    >
      <Typography variant="body2">
        This installment loan product is distinct from a credit card.
        It allows only a single use, even if you spend less than your approved limit.
        Your final terms will depend on the amount financed, but here are some
        examples of what can be offered based on your credit
        (final terms are subject to change.)
      </Typography>
      <TableContainer component={Paper} sx={{ backgroundColor: 'info.light' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell colSpan={3}>
                Loan Examples
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>Min Amount</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Max Amount</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Financing Amount</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {currencyFormat.format(min.financed_amount)}
                <sup>*</sup>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {currencyFormat.format(max.financed_amount)}
                <sup>*</sup>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Fees</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {minFee}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {maxFee}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Total Amount</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {currencyFormat.format(min.amount)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {currencyFormat.format(max.amount)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Term Length</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{min.term}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{max.term}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>APR</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {min.apr}
                %
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {max.apr}
                %
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Monthly Payment</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {currencyFormat.format(min.payment)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {currencyFormat.format(max.payment)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      { application.attributes.offer_type !== 'INSTALLMENT_NO_PROMO'
          && (
            <Alert icon={<AutoAwesomeIcon fontSize="inherit" />} severity="info">
              <Typography variant="h4" align="left">
                Additionally, you qualify for the 90-day interest-free program!
              </Typography>
              <Typography variant="body2" align="left">
                Interest will be waived if you make all of your scheduled payments on
                time and pay off your loan in full within the first 90 days. If the loan
                is not paid off in full within 90 days, you will not be eligible for the
                interest waiver and will continue to make your payments as agreed.
                Full terms and conditions will be provided prior to finalizing the loan.
              </Typography>
            </Alert>
          )}
      <Box component="form" onSubmit={handleSubmit}>
        <ButtonPrimary isLoading={isSubmitting}>
          {prequal.preliminaryRedirectUri ? 'Return to Merchant' : 'Submit Application'}
        </ButtonPrimary>
      </Box>
      <Disclaimers variant="caption" textAlign="start">
        <>
          <sup>*</sup>
          This amount is exclusive of funding fees, which range from
          {' '}
          {minFee}
          {' '}
          to
          {' '}
          {maxFee}
          , and are detailed in your financing contract.
        </>
      </Disclaimers>
    </Screen>
  );
};

export default InstallmentViewOffer;
